/**
 * When Microsoft is busy (Graph 429/503 surfaced by the API as 503 `throttled` / `unavailable`), a read is
 * tried again by itself and the page says so — "Microsoft is busy — trying again" — never a blank screen.
 * Also the page's own short log of failed requests, which the error boundary's "Copy details" carries.
 */
import { useSyncExternalStore } from 'react';

let retrying = 0;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

export function busyStarted(): void {
  retrying += 1;
  emit();
}
export function busyEnded(): void {
  retrying = Math.max(0, retrying - 1);
  emit();
}
export function useMicrosoftBusy(): boolean {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => retrying > 0,
    () => false,
  );
}

export interface ClientLogLine {
  at: string;
  level: 'error' | 'info';
  event: string;
  text: string;
}
const ring: ClientLogLine[] = [];
export function clientLog(level: ClientLogLine['level'], event: string, text: string): void {
  ring.push({ at: new Date().toISOString(), level, event, text: text.slice(0, 400) });
  if (ring.length > 20) ring.shift();
}
export function recentClientLog(): ClientLogLine[] {
  return ring.slice();
}

/** A 429, or a 503 the API marked as Microsoft being busy or unavailable. */
export function isBusyAnswer(status: number, error: string | undefined): boolean {
  return status === 429 || (status === 503 && (error === 'throttled' || error === 'unavailable'));
}

/** Waits before try n (1-based): 1 s, 2 s, 4 s. */
export const BUSY_DELAYS_MS = [1000, 2000, 4000];
